
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    MoreVertical,
    MapPin,
    Building2,
    Calendar,
    Star,
    User,
    Phone,
    Mail,
    Eye,
    Calculator,
    FileText,
    Send,
    Users,
    X
} from 'lucide-react';
import { Negociacao, PipelineStage, Profile } from '../../../types';
import { formatDate } from '../../../src/lib/utils'; // Assuming standard utils path
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { podeTransferirNegociacao, transferenciaRestritaAEquipe } from '../../../src/lib/transferenciaNegociacao';
import { podeReceberDaSupervisao } from '../../../src/lib/supervisaoComercial';
import TransferirEmMassaModal from './TransferirEmMassaModal';

interface CRMTableProps {
    negociacoes: Negociacao[];
    onOpenDetails: (negociacao: Negociacao) => void;
    stages: PipelineStage[];
    profiles: Profile[]; // To map responsibility if needed, though ID is usually on neg
    onUpdate?: () => void;
}

const CRMTable: React.FC<CRMTableProps> = ({ negociacoes, onOpenDetails, stages, profiles, onUpdate }) => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    // adm/gerente pelo papel, quem tem a permissão "Transferir negociações" ou quem
    // supervisiona o Comercial (este só para Closers, BDRs e ele mesmo, #6521).
    const podeTransferir = podeTransferirNegociacao(profile);
    const destinosDaTransferencia = transferenciaRestritaAEquipe(profile)
        ? profiles.filter(p => podeReceberDaSupervisao(profile, p))
        : profiles;

    const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Mock state for contract generation to demo "Enviar" button logic in table
    const [generatedContracts, setGeneratedContracts] = useState<number[]>([]);

    // Seleção em massa para transferência de responsável (ver podeTransferir)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);

    // Remove da seleção itens que saíram da lista filtrada atual
    useEffect(() => {
        setSelectedIds(prev => {
            const validIds = new Set(negociacoes.map(n => n.id));
            const next = new Set(Array.from(prev).filter(id => validIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [negociacoes]);

    const toggleSelectOne = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const allSelected = negociacoes.length > 0 && selectedIds.size === negociacoes.length;

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(negociacoes.map(n => n.id)));
    };

    const handleBulkTransfer = async (targetUserId: string) => {
        const targetUser = profiles.find(p => p.id === targetUserId);
        const ids = Array.from(selectedIds);
        if (ids.length === 0 || !targetUser) return;

        setBulkSaving(true);
        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({ responsavel_id: targetUserId })
                .in('id', ids);

            if (error) throw error;

            const logs = ids.map(id => {
                const neg = negociacoes.find(n => n.id === id);
                return {
                    negociacao_id: id,
                    user_id: profile?.id,
                    acao: `transferiu em massa o responsável para ${targetUser.full_name}`,
                    detalhes: JSON.stringify({ previous: neg?.responsavel_id, new: targetUserId }),
                };
            });
            await supabase.from('negociacao_logs').insert(logs);
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'transferir_negociacoes_em_massa', origem: 'acao' });

            addToast(`${ids.length} negociação(ões) transferida(s) para ${targetUser.full_name}`, 'success');
            setShowBulkModal(false);
            setSelectedIds(new Set());
            onUpdate?.();
        } catch (error) {
            console.error('Error bulk transferring negotiations:', error);
            addToast('Erro ao transferir negociações em massa.', 'error');
        } finally {
            setBulkSaving(false);
        }
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = () => setDropdownOpen(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const toggleDropdown = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.right + window.scrollX - 180, // Adjust for width
        });
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    const handleAction = (action: string, negId: number) => {
        if (action === 'simulate') {
            alert('Simulação de economia iniciada! (Funcionalidade em breve)');
        } else if (action === 'generate') {
            alert('Gerando contrato... (Simulação)');
            setTimeout(() => {
                setGeneratedContracts(prev => [...prev, negId]);
                alert('Contrato gerado!');
            }, 1000);
        } else if (action === 'send') {
            alert('Contrato enviado para assinatura! (Funcionalidade em breve)');
        }
        setDropdownOpen(null);
    };

    const getStageName = (stageIdOrName: string) => {
        const stage = stages.find(s => s.name === stageIdOrName || s.id === stageIdOrName);
        return stage ? stage.name : stageIdOrName;
    };

    const getStageColor = (stageIdOrName: string) => {
        const stage = stages.find(s => s.name === stageIdOrName || s.id === stageIdOrName);
        return stage?.color || '#9ca3af'; // Default gray
    };

    const getResponsibleName = (id?: string) => {
        if (!id) return 'Sem responsável';
        const profile = profiles.find(p => p.id === id);
        return profile ? (profile.full_name || 'Desconhecido') : 'Desconhecido';
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex-1 flex flex-col min-h-0">
            <div className="overflow-auto custom-scrollbar flex-1">
                <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                        <tr>
                            {podeTransferir && (
                                <th className="px-4 py-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        className="rounded text-primary focus:ring-primary"
                                    />
                                </th>
                            )}
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Negociação / Cliente</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Etapa</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Contato</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Responsável</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right">Consumo Médio</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-center">Avaliação</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Criado em</th>
                            <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                        {negociacoes.length === 0 ? (
                            <tr>
                                <td colSpan={podeTransferir ? 9 : 8} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">
                                    Nenhuma negociação encontrada com os filtros atuais.
                                </td>
                            </tr>
                        ) : (
                            negociacoes.map((neg) => (
                                <tr
                                    key={neg.id}
                                    onClick={() => onOpenDetails(neg)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer group"
                                >
                                    {/* Seleção em massa */}
                                    {podeTransferir && (
                                        <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(neg.id)}
                                                onChange={() => toggleSelectOne(neg.id)}
                                                className="rounded text-primary focus:ring-primary"
                                            />
                                        </td>
                                    )}

                                    {/* Negociação / Cliente */}
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                                                {neg.titulo}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                {neg.cliente_nome}
                                                {neg.numero_uc && <span className="text-[10px] bg-gray-100 dark:bg-gray-700 px-1.5 rounded py-0.5">{neg.numero_uc}</span>}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Etapa */}
                                    <td className="px-6 py-4">
                                        <span
                                            className="px-2.5 py-1 rounded-full text-xs font-semibold text-white/90 shadow-sm whitespace-nowrap"
                                            style={{ backgroundColor: getStageColor(neg.stage) }}
                                        >
                                            {getStageName(neg.stage)}
                                        </span>
                                    </td>

                                    {/* Contato */}
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1">
                                            {neg.whatsapp ? (
                                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                                                    <Phone size={12} className="text-emerald-500" />
                                                    {neg.whatsapp}
                                                </div>
                                            ) : null}
                                            {neg.email ? (
                                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                                                    <Mail size={12} className="text-blue-500" />
                                                    <span className="truncate max-w-[150px]" title={neg.email}>{neg.email}</span>
                                                </div>
                                            ) : null}
                                            {!neg.whatsapp && !neg.email && <span className="text-xs text-gray-400">-</span>}
                                        </div>
                                    </td>

                                    {/* Responsável */}
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                {getResponsibleName(neg.responsavel_id).charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[120px]">
                                                {getResponsibleName(neg.responsavel_id)}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Consumo */}
                                    <td className="px-6 py-4 text-right">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {neg.consumo_medio_kwh ? `${Number(neg.consumo_medio_kwh).toLocaleString('pt-BR')} kWh` : '-'}
                                        </span>
                                    </td>

                                    {/* Avaliação */}
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-0.5">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <Star
                                                    key={i}
                                                    size={12}
                                                    className={`${i < (neg.avaliacao_estrelas || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
                                                />
                                            ))}
                                        </div>
                                    </td>

                                    {/* Criado em */}
                                    <td className="px-6 py-4">
                                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                            <Calendar size={12} />
                                            {formatDate(neg.created_at)}
                                        </span>
                                    </td>

                                    {/* Ações */}
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={(e) => toggleDropdown(e, neg.id)}
                                            className={`p-2 rounded-lg transition-colors ${dropdownOpen === neg.id ? 'bg-primary/10 text-primary' : 'text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                        >
                                            <MoreVertical size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Dropdown Portal */}
            {dropdownOpen && createPortal(
                <div
                    className="fixed z-50 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1 w-48 animate-fade-in"
                    style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={() => {
                            const neg = negociacoes.find(n => n.id === dropdownOpen);
                            if (neg) onOpenDetails(neg);
                            setDropdownOpen(null);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                    >
                        <Eye size={14} className="text-gray-400" />
                        Ver detalhes
                    </button>

                    <button
                        onClick={() => handleAction('simulate', dropdownOpen)}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                    >
                        <Calculator size={14} className="text-emerald-500" />
                        Simular economia
                    </button>

                    <button
                        onClick={() => handleAction('generate', dropdownOpen)}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                    >
                        <FileText size={14} className="text-blue-500" />
                        Gerar contrato
                    </button>

                    {generatedContracts.includes(dropdownOpen) && (
                        <button
                            onClick={() => handleAction('send', dropdownOpen)}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                        >
                            <Send size={14} className="text-indigo-500" />
                            Enviar contrato
                        </button>
                    )}

                </div>,
                document.body
            )}

            {/* Footer / Pagination placeholder if needed later */}
            <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-xs text-gray-400 text-center">
                Total de {negociacoes.length} negociações listadas
            </div>

            {/* Barra de ação em massa — transferência de responsável */}
            {podeTransferir && selectedIds.size > 0 && (
                <div className="fixed inset-x-0 bottom-0 z-[1000] px-3 pb-3 pointer-events-none flex justify-center">
                    <div className="pointer-events-auto w-full sm:w-auto bg-gray-900 dark:bg-gray-950 text-white rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 animate-fade-in">
                        <div className="flex items-center gap-2 pr-1">
                            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold shrink-0">{selectedIds.size}</span>
                            <p className="text-xs font-bold whitespace-nowrap">
                                {selectedIds.size === 1 ? '1 negociação selecionada' : `${selectedIds.size} negociações selecionadas`}
                            </p>
                        </div>
                        <div className="h-7 w-px bg-white/15 hidden sm:block" />
                        <button
                            onClick={() => setShowBulkModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/20 transition-colors"
                        >
                            <Users size={14} /> Transferir
                        </button>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            className="p-1.5 rounded-full text-white/70 hover:bg-white/10 transition-colors"
                            title="Limpar seleção"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            {showBulkModal && (
                <TransferirEmMassaModal
                    count={selectedIds.size}
                    profiles={destinosDaTransferencia}
                    salvando={bulkSaving}
                    onConfirm={handleBulkTransfer}
                    onClose={() => setShowBulkModal(false)}
                />
            )}
        </div>
    );
};

export default CRMTable;
